import { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  Tooltip,
  Autocomplete,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpIcon from '@mui/icons-material/HelpOutline';
import type { BpmnElement } from '../../hooks/useBpmnModeler';

interface FlowablePropertiesEditorProps {
  element: BpmnElement;
  onUpdateProperty: (property: string, value: unknown) => void;
}

type ServiceTaskImplementationType = 'class' | 'expression' | 'delegateExpression' | 'external';

// Predefined options for task assignment
const ASSIGNEE_OPTIONS = [
  { value: '${initiator}', label: '${initiator} - Prozessstarter' },
  { value: '${lastAssignee}', label: '${lastAssignee} - Vorheriger Bearbeiter' },
];

const CANDIDATE_USER_OPTIONS = [
  { value: '${initiator}', label: '${initiator} - Prozessstarter' },
];

const CANDIDATE_GROUP_OPTIONS = [
  { value: 'AUTHORITY', label: 'AUTHORITY - Behörden-Mitarbeiter' },
  { value: 'EMPLOYEE', label: 'EMPLOYEE - Sachbearbeiter' },
  { value: 'ADMIN', label: 'ADMIN - Administratoren' },
  { value: 'MANAGER', label: 'MANAGER - Abteilungsleiter' },
  { value: 'APPROVER', label: 'APPROVER - Genehmiger' },
];

export function FlowablePropertiesEditor({
  element,
  onUpdateProperty,
}: FlowablePropertiesEditorProps) {
  const bo = element?.businessObject;
  const elementType = element?.type || '';

  // Determine implementation type for service task
  const getImplementationType = (): ServiceTaskImplementationType => {
    if (bo?.class) return 'class';
    if (bo?.expression) return 'expression';
    if (bo?.delegateExpression) return 'delegateExpression';
    if (bo?.topic) return 'external';
    return 'class';
  };

  const [implType, setImplType] = useState<ServiceTaskImplementationType>(getImplementationType());

  const handleImplementationTypeChange = (type: ServiceTaskImplementationType) => {
    setImplType(type);
    // Clear other implementation properties
    if (type !== 'class') onUpdateProperty('class', undefined);
    if (type !== 'expression') onUpdateProperty('expression', undefined);
    if (type !== 'delegateExpression') onUpdateProperty('delegateExpression', undefined);
    if (type !== 'external') onUpdateProperty('topic', undefined);
  };

  // Guard against missing element or businessObject
  if (!element || !bo) {
    return null;
  }

  // Service Task Properties
  if (elementType === 'bpmn:ServiceTask') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="subtitle2" color="primary">
          Flowable Service Task
        </Typography>

        <FormControl fullWidth size="small">
          <InputLabel>Implementierung</InputLabel>
          <Select
            value={implType}
            label="Implementierung"
            onChange={(e) => handleImplementationTypeChange(e.target.value as ServiceTaskImplementationType)}
          >
            <MenuItem value="class">Java-Klasse</MenuItem>
            <MenuItem value="expression">Expression</MenuItem>
            <MenuItem value="delegateExpression">Delegate Expression</MenuItem>
            <MenuItem value="external">External Worker (Topic)</MenuItem>
          </Select>
        </FormControl>

        {implType === 'class' && (
          <TextField
            label="Java-Klasse"
            size="small"
            fullWidth
            value={bo.class || ''}
            onChange={(e) => onUpdateProperty('class', e.target.value || undefined)}
            placeholder="com.example.MyDelegate"
            helperText="Vollqualifizierter Klassenname"
          />
        )}

        {implType === 'expression' && (
          <TextField
            label="Expression"
            size="small"
            fullWidth
            value={bo.expression || ''}
            onChange={(e) => onUpdateProperty('expression', e.target.value || undefined)}
            placeholder="${myBean.doSomething(execution)}"
            helperText="UEL Expression"
          />
        )}

        {implType === 'delegateExpression' && (
          <TextField
            label="Delegate Expression"
            size="small"
            fullWidth
            value={bo.delegateExpression || ''}
            onChange={(e) => onUpdateProperty('delegateExpression', e.target.value || undefined)}
            placeholder="${myDelegate}"
            helperText="Bean-Name als Expression"
          />
        )}

        {implType === 'external' && (
          <TextField
            label="Topic"
            size="small"
            fullWidth
            value={bo.topic || ''}
            onChange={(e) => onUpdateProperty('topic', e.target.value || undefined)}
            placeholder="my-topic"
            helperText="External Worker Topic"
          />
        )}

        <TextField
          label="Ergebnis-Variable"
          size="small"
          fullWidth
          value={bo.resultVariable || ''}
          onChange={(e) => onUpdateProperty('resultVariable', e.target.value || undefined)}
          placeholder="result"
          helperText="Variable fuer das Ergebnis"
        />

        <FormControlLabel
          control={
            <Switch
              checked={bo.$attrs?.['flowable:async'] === 'true' || bo.$attrs?.['flowable:async'] === true}
              onChange={(e) => onUpdateProperty('flowable:async', e.target.checked)}
              size="small"
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              Asynchron
              <Tooltip title="Task wird asynchron ausgefuehrt">
                <HelpIcon fontSize="small" color="action" />
              </Tooltip>
            </Box>
          }
        />
      </Box>
    );
  }

  // User Task Properties
  if (elementType === 'bpmn:UserTask') {
    // Parse current values (cast to string since they come from XML attributes)
    const currentAssignee = String(bo.$attrs?.['flowable:assignee'] || '');
    const currentCandidateUsers = String(bo.$attrs?.['flowable:candidateUsers'] || '');
    const currentCandidateGroups = String(bo.$attrs?.['flowable:candidateGroups'] || '');

    // Parse comma-separated values into arrays
    const candidateUsersArray = currentCandidateUsers
      ? currentCandidateUsers.split(',').map((s: string) => s.trim()).filter(Boolean)
      : [];
    const candidateGroupsArray = currentCandidateGroups
      ? currentCandidateGroups.split(',').map((s: string) => s.trim()).filter(Boolean)
      : [];

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="subtitle2" color="primary">
          Flowable User Task
        </Typography>

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2">Zuweisung</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Assignee - Single selection with freeSolo */}
              <Autocomplete
                freeSolo
                options={ASSIGNEE_OPTIONS.map(opt => opt.value)}
                value={currentAssignee}
                onChange={(_, newValue) => {
                  onUpdateProperty('flowable:assignee', newValue || undefined);
                }}
                onInputChange={(_, newInputValue, reason) => {
                  if (reason === 'input') {
                    onUpdateProperty('flowable:assignee', newInputValue || undefined);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Bearbeiter (Assignee)"
                    size="small"
                    placeholder="${initiator} oder Benutzername"
                    helperText="Direkte Zuweisung an einen Benutzer"
                  />
                )}
                renderOption={({ key, ...props }, option) => {
                  const optionData = ASSIGNEE_OPTIONS.find(o => o.value === option);
                  return (
                    <li key={key} {...props}>
                      <Box>
                        <Typography variant="body2">{option}</Typography>
                        {optionData && (
                          <Typography variant="caption" color="text.secondary">
                            {optionData.label.split(' - ')[1]}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  );
                }}
              />

              {/* Candidate Users - Multiple selection with freeSolo */}
              <Autocomplete
                multiple
                freeSolo
                options={CANDIDATE_USER_OPTIONS.map(opt => opt.value)}
                value={candidateUsersArray}
                onChange={(_, newValue) => {
                  const valueString = newValue.join(', ');
                  onUpdateProperty('flowable:candidateUsers', valueString || undefined);
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      size="small"
                      {...getTagProps({ index })}
                      key={option}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Kandidaten-Benutzer"
                    size="small"
                    placeholder="Benutzer hinzufügen..."
                    helperText="Benutzer, die diese Aufgabe übernehmen können"
                  />
                )}
                renderOption={({ key, ...props }, option) => {
                  const optionData = CANDIDATE_USER_OPTIONS.find(o => o.value === option);
                  return (
                    <li key={key} {...props}>
                      <Box>
                        <Typography variant="body2">{option}</Typography>
                        {optionData && (
                          <Typography variant="caption" color="text.secondary">
                            {optionData.label.split(' - ')[1]}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  );
                }}
              />

              {/* Candidate Groups - Multiple selection with freeSolo */}
              <Autocomplete
                multiple
                freeSolo
                options={CANDIDATE_GROUP_OPTIONS.map(opt => opt.value)}
                value={candidateGroupsArray}
                onChange={(_, newValue) => {
                  const valueString = newValue.join(', ');
                  onUpdateProperty('flowable:candidateGroups', valueString || undefined);
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const optionData = CANDIDATE_GROUP_OPTIONS.find(o => o.value === option);
                    return (
                      <Chip
                        label={optionData ? optionData.label.split(' - ')[0] : option}
                        size="small"
                        color="primary"
                        variant="outlined"
                        {...getTagProps({ index })}
                        key={option}
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Kandidaten-Gruppen"
                    size="small"
                    placeholder="Gruppe auswählen..."
                    helperText="Gruppen, die diese Aufgabe übernehmen können"
                  />
                )}
                renderOption={({ key, ...props }, option) => {
                  const optionData = CANDIDATE_GROUP_OPTIONS.find(o => o.value === option);
                  return (
                    <li key={key} {...props}>
                      <Box>
                        <Typography variant="body2">{option}</Typography>
                        {optionData && (
                          <Typography variant="caption" color="text.secondary">
                            {optionData.label.split(' - ')[1]}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  );
                }}
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2">Formular & Fristen</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Form Key"
                size="small"
                fullWidth
                value={bo.$attrs?.['flowable:formKey'] || ''}
                onChange={(e) => onUpdateProperty('flowable:formKey', e.target.value || undefined)}
                placeholder="myForm"
                helperText="Formular-Schluessel"
              />

              <TextField
                label="Faelligkeitsdatum"
                size="small"
                fullWidth
                value={bo.$attrs?.['flowable:dueDate'] || ''}
                onChange={(e) => onUpdateProperty('flowable:dueDate', e.target.value || undefined)}
                placeholder="${dateTime().plusDays(3).toDate()}"
                helperText="ISO-Datum oder Expression"
              />

              <TextField
                label="Prioritaet"
                size="small"
                fullWidth
                type="number"
                value={bo.$attrs?.['flowable:priority'] || ''}
                onChange={(e) => onUpdateProperty('flowable:priority', e.target.value || undefined)}
                placeholder="50"
                helperText="Numerische Prioritaet (0-100)"
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2">Erweitert</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Skip Expression"
                size="small"
                fullWidth
                value={bo.$attrs?.['flowable:skipExpression'] || ''}
                onChange={(e) => onUpdateProperty('flowable:skipExpression', e.target.value || undefined)}
                placeholder="${skip == true}"
                helperText="Bedingung zum Ueberspringen"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={bo.$attrs?.['flowable:async'] === 'true' || bo.$attrs?.['flowable:async'] === true}
                    onChange={(e) => onUpdateProperty('flowable:async', e.target.checked)}
                    size="small"
                  />
                }
                label="Asynchron"
              />
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  }

  // Script Task Properties
  if (elementType === 'bpmn:ScriptTask') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="subtitle2" color="primary">
          Flowable Script Task
        </Typography>

        <FormControl fullWidth size="small">
          <InputLabel>Skript-Sprache</InputLabel>
          <Select
            value={bo.scriptFormat || 'groovy'}
            label="Skript-Sprache"
            onChange={(e) => onUpdateProperty('scriptFormat', e.target.value)}
          >
            <MenuItem value="groovy">Groovy</MenuItem>
            <MenuItem value="javascript">JavaScript</MenuItem>
            <MenuItem value="juel">JUEL</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Skript"
          size="small"
          fullWidth
          multiline
          rows={6}
          value={bo.script || ''}
          onChange={(e) => onUpdateProperty('script', e.target.value || undefined)}
          placeholder="// Ihr Skript hier"
          sx={{ fontFamily: 'monospace' }}
        />

        <TextField
          label="Ergebnis-Variable"
          size="small"
          fullWidth
          value={bo.$attrs?.['flowable:resultVariable'] || ''}
          onChange={(e) => onUpdateProperty('flowable:resultVariable', e.target.value || undefined)}
          placeholder="scriptResult"
        />

        <FormControlLabel
          control={
            <Switch
              checked={bo.$attrs?.['flowable:autoStoreVariables'] === 'true'}
              onChange={(e) => onUpdateProperty('flowable:autoStoreVariables', e.target.checked)}
              size="small"
            />
          }
          label="Variablen automatisch speichern"
        />
      </Box>
    );
  }

  // Sequence Flow (Condition)
  if (elementType === 'bpmn:SequenceFlow') {
    const hasCondition = !!bo.conditionExpression;
    const conditionBody = bo.conditionExpression?.body || '';

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="subtitle2" color="primary">
          Sequenzfluss-Bedingung
        </Typography>

        <Typography variant="caption" color="text.secondary">
          Von: {bo.sourceRef?.id} → Nach: {bo.targetRef?.id}
        </Typography>

        <TextField
          label="Bedingung (Expression)"
          size="small"
          fullWidth
          multiline
          rows={3}
          value={conditionBody}
          onChange={(e) => {
            // This needs special handling through modeling API
            onUpdateProperty('conditionExpression', e.target.value ? {
              $type: 'bpmn:FormalExpression',
              body: e.target.value,
            } : undefined);
          }}
          placeholder="${approved == true}"
          helperText="UEL-Expression fuer bedingte Ausfuehrung"
        />

        {hasCondition && (
          <Typography variant="caption" color="success.main">
            Bedingter Fluss aktiv
          </Typography>
        )}
      </Box>
    );
  }

  // Gateway (Default Flow)
  if (elementType === 'bpmn:ExclusiveGateway' || elementType === 'bpmn:InclusiveGateway') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="subtitle2" color="primary">
          Gateway-Eigenschaften
        </Typography>

        {bo.default && (
          <Typography variant="body2">
            Standard-Fluss: <strong>{bo.default.id}</strong>
          </Typography>
        )}

        <Typography variant="caption" color="text.secondary">
          Der Standard-Fluss wird ausgefuehrt, wenn keine andere Bedingung zutrifft.
          Setzen Sie ihn durch Rechtsklick auf einen ausgehenden Sequenzfluss.
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={bo.$attrs?.['flowable:async'] === 'true'}
              onChange={(e) => onUpdateProperty('flowable:async', e.target.checked)}
              size="small"
            />
          }
          label="Asynchron"
        />
      </Box>
    );
  }

  // Call Activity
  if (elementType === 'bpmn:CallActivity') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="subtitle2" color="primary">
          Call Activity
        </Typography>

        <TextField
          label="Aufgerufener Prozess"
          size="small"
          fullWidth
          value={bo.$attrs?.['flowable:calledElement'] || bo.calledElement || ''}
          onChange={(e) => onUpdateProperty('flowable:calledElement', e.target.value || undefined)}
          placeholder="subProcessKey"
          helperText="Process Definition Key des aufzurufenden Prozesses"
        />

        <FormControl fullWidth size="small">
          <InputLabel>Binding</InputLabel>
          <Select
            value={bo.$attrs?.['flowable:calledElementBinding'] || 'latest'}
            label="Binding"
            onChange={(e) => onUpdateProperty('flowable:calledElementBinding', e.target.value)}
          >
            <MenuItem value="latest">Letzte Version</MenuItem>
            <MenuItem value="deployment">Deployment-Version</MenuItem>
            <MenuItem value="version">Bestimmte Version</MenuItem>
          </Select>
        </FormControl>

        {bo.$attrs?.['flowable:calledElementBinding'] === 'version' && (
          <TextField
            label="Version"
            size="small"
            fullWidth
            value={bo.$attrs?.['flowable:calledElementVersion'] || ''}
            onChange={(e) => onUpdateProperty('flowable:calledElementVersion', e.target.value || undefined)}
            placeholder="1"
          />
        )}

        <FormControlLabel
          control={
            <Switch
              checked={bo.$attrs?.['flowable:inheritVariables'] === 'true'}
              onChange={(e) => onUpdateProperty('flowable:inheritVariables', e.target.checked)}
              size="small"
            />
          }
          label="Variablen vererben"
        />
      </Box>
    );
  }

  // Process (root element)
  if (elementType === 'bpmn:Process' || elementType === 'bpmn:Participant') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="subtitle2" color="primary">
          Prozess-Eigenschaften
        </Typography>

        <TextField
          label="Kandidaten-Starter (Benutzer)"
          size="small"
          fullWidth
          value={bo.$attrs?.['flowable:candidateStarterUsers'] || ''}
          onChange={(e) => onUpdateProperty('flowable:candidateStarterUsers', e.target.value || undefined)}
          placeholder="user1, user2"
          helperText="Benutzer, die den Prozess starten koennen"
        />

        <TextField
          label="Kandidaten-Starter (Gruppen)"
          size="small"
          fullWidth
          value={bo.$attrs?.['flowable:candidateStarterGroups'] || ''}
          onChange={(e) => onUpdateProperty('flowable:candidateStarterGroups', e.target.value || undefined)}
          placeholder="managers"
          helperText="Gruppen, die den Prozess starten koennen"
        />

        <TextField
          label="History Time to Live"
          size="small"
          fullWidth
          value={bo.$attrs?.['flowable:historyTimeToLive'] || ''}
          onChange={(e) => onUpdateProperty('flowable:historyTimeToLive', e.target.value || undefined)}
          placeholder="P180D"
          helperText="ISO-8601 Duration (z.B. P180D fuer 180 Tage)"
        />
      </Box>
    );
  }

  // Default: No Flowable properties for this element type
  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="body2" color="text.secondary">
        Keine Flowable-spezifischen Eigenschaften fuer diesen Elementtyp verfuegbar.
      </Typography>
    </Box>
  );
}
